import type { AxiosProgressEvent } from "axios";
import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import type LxcError from "~/core/models/LxcError.class";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import type { FirmwareUpdatePayload } from "~/modules/firmware/models/FirmwareUpdatePayload.interface";
import organizationService from "~/modules/organization/services/organization.service";

export class FirmwareService extends RestService {
  protected FIRMWARES = "firmwares";

  /**
   * Retrieve the firmwares of a business unit
   * @param businessUnitCode
   * @param page
   * @param pageSize
   * @param searchParams
   * @param sort
   * @param params
   */
  async getBusinessUnitFirmwares({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<Firmware, [] | undefined> | LxcError
  > {
    return this.$apiV4.get(
      this.getBusinessUnitFirmwaresPath(businessUnitCode),
      {
        params: {
          page,
          pageSize,
          filter: searchParams,
          sort,
          ...Object.fromEntries(params || new Map()),
        },
      },
    );
  }

  /**
   * Retrieve the firmwares of an organization
   * @param organizationCode
   * @param page
   * @param pageSize
   * @param searchParams
   * @param sort
   * @param params
   */
  async getOrganizationFirmwares({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<Firmware, [] | undefined> | LxcError
  > {
    return this.$apiV4.get(
      this.getOrganizationFirmwaresPath(organizationCode),
      {
        params: {
          page,
          pageSize,
          filter: searchParams,
          sort,
          ...Object.fromEntries(params || new Map()),
        },
      },
    );
  }

  /**
   * Retrieve a firmware of a business unit
   * @param businessUnitCode
   * @param uuid
   */
  async getBusinessUnitFirmware({
    businessUnitCode,
    uuid,
  }: {
    businessUnitCode: number;
    uuid: string;
  }): Promise<Firmware> {
    return this.$apiV4.get(
      `${this.getBusinessUnitFirmwaresPath(businessUnitCode)}/${uuid}`,
    );
  }

  /**
   * Retrieve a firmware of an organization
   * @param organizationCode
   * @param uuid
   */
  async getOrganizationFirmware({
    organizationCode,
    uuid,
  }: {
    organizationCode: number;
    uuid: string;
  }): Promise<Firmware> {
    return this.$apiV4.get(
      `${this.getOrganizationFirmwaresPath(organizationCode)}/${uuid}`,
    );
  }

  async getOrganizationFirmwareUrls(
    organizationCode: number,
    uuid: string,
    operationModelUid: string,
  ): Promise<Record<string, unknown>> {
    return this.$apiV4.get(
      `${this.getOrganizationFirmwaresPath(organizationCode)}/${uuid}/urls`,
      {
        params: {
          operationModelUid,
        },
      },
    );
  }

  /**
   * Upload a firmware file in a business unit
   * @param businessUnitCode
   * @param firmwareFile
   * @param progress
   */
  async uploadBusinessUnitFirmware(
    businessUnitCode: number,
    firmwareFile: File,
    progress: Ref<number | undefined | null>,
  ): Promise<Firmware> {
    const formData = new FormData();
    formData.append("firmwareFile", firmwareFile);
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        progress.value = progressEvent.total
          ? (progressEvent.loaded / progressEvent.total) * 100
          : 0;
      },
      timeout: Infinity,
    };

    return this.$apiV4.post(
      `${this.getBusinessUnitFirmwaresPath(businessUnitCode)}/upload`,
      formData,
      config,
    );
  }

  /**
   * Create a firmware in a business unit
   * @param businessUnitCode
   * @param firmware
   */
  async createBusinessUnitFirmware(
    businessUnitCode: number,
    firmware: Firmware,
  ): Promise<Firmware> {
    return this.$apiV4.post(
      this.getBusinessUnitFirmwaresPath(businessUnitCode),
      firmware,
    );
  }

  /**
   * Activate a firmware of a business unit
   * @param businessUnitCode
   * @param uuid
   */
  async activateBusinessUnitFirmware(
    businessUnitCode: number | undefined,
    uuid: string,
  ): Promise<void> {
    return this.$apiV4.patch(
      `${this.getBusinessUnitFirmwaresPath(businessUnitCode)}/${uuid}/activate`,
    );
  }

  /**
   * Deactivate a firmware of a business unit
   * @param businessUnitCode
   * @param uuid
   */
  async deactivateBusinessUnitFirmware(
    businessUnitCode: number | undefined,
    uuid: string,
  ): Promise<void> {
    return this.$apiV4.patch(
      `${this.getBusinessUnitFirmwaresPath(businessUnitCode)}/${uuid}/deactivate`,
    );
  }

  /**
   * Update an organization firmware name and description
   * @param businessUnitCode
   * @param uuid
   * @param firmwareUpdatePayload
   */
  async updateBusinessUnitFirmwareNameAndDescription(
    businessUnitCode: number | undefined,
    uuid: string,
    firmwareUpdatePayload: FirmwareUpdatePayload,
  ): Promise<Firmware> {
    return this.$apiV4.patch(
      `${this.getBusinessUnitFirmwaresPath(businessUnitCode)}/${uuid}`,
      firmwareUpdatePayload,
    );
  }

  /**
   * Delete a firmware of a business unit
   * @param businessUnitCode
   * @param uuid
   */
  async deleteBusinessUnitFirmware(
    businessUnitCode: number | undefined,
    uuid: string,
  ): Promise<void> {
    return this.$apiV4.delete(
      `${this.getBusinessUnitFirmwaresPath(businessUnitCode)}/${uuid}`,
    );
  }

  private getBusinessUnitFirmwaresPath(businessUnitCode?: number): string {
    return `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.FIRMWARES}`;
  }

  private getOrganizationFirmwaresPath(organizationCode?: number): string {
    return `${organizationService.getOrganizationPath(organizationCode)}/${this.FIRMWARES}`;
  }
}

export default new FirmwareService();
